import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { PageHeaderMenu } from '@app/components/pr-page/pr-page-header-menu/pr-page-header-menu.component';
import {
  ProductsTypeEnum,
  Question,
} from '@app/forms/subscription/definition/interface';
import { environment } from '@environments/environment';
import * as _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { PledgeRead } from '../definition/interface/Pledge';
import { CommandEnum } from '../definition/interface/enum';
import { SubscriptionFacadeService } from '../services/subscription-facade.service';
import { getCommands } from '../services/utile';

export class BaseSubscriptionComponent {
  menus: PageHeaderMenu;
  step = 1;
  questions: Question[];
  question: Question;
  entity: any;
  questionConsultant;
  toggle = false;
  protected _loading$: Observable<boolean>;
  protected _error$: Observable<any>;
  question$: any;
  pictureFile: string = '';
  consultantBackUrl = environment.servers.resources.url || '';
  questionTotal = 0;
  qsIdx = 0;
  isConsultant = false;
  user: any;
  clickSubject: Subject<any> = new Subject();
  noPledgeFound: boolean = false;

  constructor(public subscriptionFacade: SubscriptionFacadeService) {
    this.menus = this.subscriptionFacade.getMenu();
    this._loading$ = this.subscriptionFacade.loading$;
    this._error$ = this.subscriptionFacade.error$;
  }
  protected handlePledgeUpdate(
    productType: any,
    pledge: any,
    user: UserAuthentified,
  ) {
    if (pledge?.parameters) {
      pledge.parameters.riskTotalLoss = true;
    }
    this.setQuestionConsultantFirstTime(productType, pledge, user);
    if (user?.sessionConsultant) {
      this.displayAndSkipQuestionForConsultant(pledge, user, productType);
    } else {
      this.subscriptionFacade.emitPledgeAndEntity(pledge);
    }
    return pledge;
  }
  protected handleNextQuestionGeneration(
    questions: Question[],
    productType: ProductsTypeEnum,
  ) {
    const currentQuestion = questions?.[this.step - 1];
    if (currentQuestion) {
      const index = questions.findIndex(q => q.id == currentQuestion.id);
      const nextQuestion = questions[index + 1];
      if (nextQuestion) {
        this.setGenerateSubscriptionFileCommand(nextQuestion, productType);
      }
    }
    return questions;
  }
  protected updateQuestionState(questions: Question[]) {
    const currentQuestion = questions?.[this.step - 1];
    this.questionTotal = this.getQuestionByCategory(
      questions,
      currentQuestion,
    )?.length;
    this.qsIdx = this.findIndexQuestionCategory(questions, currentQuestion);
    if (currentQuestion) {
      this.subscriptionFacade.setStepName(currentQuestion.name);
      this.subscriptionFacade.setQuestion(currentQuestion);
    }
    return currentQuestion;
  }
  protected handleQuestionsChanged(
    user: UserAuthentified,
    productType: ProductsTypeEnum,
  ) {
    if (this.entity) {
      if (user?.sessionConsultant) {
        const filteredQuestions = this.filterByProduct(
          productType,
          this.entity,
          user,
        );

        this.subscriptionFacade.setQuestions(
          _.sortBy(filteredQuestions, question => {
            return question.id;
          }),
        );
      } else {
        let questions = this.getQuestions(user, productType, this.entity);

        this.subscriptionFacade.setQuestions(questions);
      }
    }
    return this.subscriptionFacade.questionsChanged$;
  }
  protected handleStepChange(step: number) {
    if (step !== null || step !== undefined) this.step = step;
    return this.subscriptionFacade.pledgeChanged$;
  }
  protected handleEntityUpdate(entity: PledgeRead) {
    if (entity?.owners) {
      this.entity = entity;
    }
  }
  protected handleUserUpdate(user: UserAuthentified) {
    this.user = user;
    this.isConsultant = !!user.sessionConsultant;
  }
  setGenerateSubscriptionFileCommand(
    question: Question,
    productType: string,
  ): void {
    const { name } = question || {};
    const eligibleQuestions = [
      'signatureElectronic',
      'sendDocument',
      'documentCompliance',
      'leadCompletionSubscription',
    ];

    if (eligibleQuestions.includes(name)) {
      if (productType === 'PRESTATION') {
        this.subscriptionFacade.setCommand(
          getCommands().filter(c => c !== CommandEnum.RECALCULATE_RISK_PROFIL),
        );
        return;
      }

      this.subscriptionFacade.setCommand(getCommands());
    }
  }

  protected getQuestions(
    user: UserAuthentified,
    productType: string,
    pledge: PledgeRead,
  ): Question[] {
    let questions: Question[] = [];
    if (user.sessionConsultant) {
      const consultantQuestions =
        this.subscriptionFacade.getConsultantQuestions({
          product: productType,
          entity: pledge,
          user,
        });

      const callbackQuestions = this.getConsultantQuestionsCallback(
        this.questionConsultant?.length ? [...this.questionConsultant] : [],
        pledge,
        user,
      );

      const filteredQuestions = this.questionConsultant
        ? _.uniqBy(
            _.concat(callbackQuestions, consultantQuestions),
            question => question.name,
          )
        : consultantQuestions;

      questions = _.sortBy(filteredQuestions, question => {
        return question.id;
      });
    } else {
      const clientQuestions = this.subscriptionFacade.getClientQuestions({
        product: productType,
        entity: pledge,
      });
      questions = _.sortBy(clientQuestions, question => {
        return question.id;
      });
    }

    return questions;
  }

  initStep(): void {
    const step = this.subscriptionFacade.getPledge().step;
    /**
     * convert to number ~~ > parseInt
     */
    step && this.subscriptionFacade.setStep(~~step);
  }

  /*
   * EXPLANATION: this function is used to get the questions for the consultant and exclude the questions that are callbackBasedOn
   */
  getConsultantQuestionsCallback(
    questions: Question[],
    entity,
    user: UserAuthentified = undefined,
  ): Question[] {
    // isConsultant est un gate de rôle (cf. InMemoryDataService.getClientQuestions : !q.isConsultant),
    // pas un raccourci de visibilité : il ne doit jamais court-circuiter alwaysShow/callbackBasedOn,
    // sinon une Question isConsultant avec un callbackBasedOn (ex. les 4 slides "Taux par support"
    // par opération) s'affiche toujours quel que soit son propre callbackBasedOn.
    return questions.filter(
      q =>
        (q.alwaysShow && !!q.alwaysShow(entity, user)) ||
        ((!q.callbackBasedOn || !q.callbackBasedOn(entity, user)) &&
          !q.alwaysSkip),
    );
  }
  // TODO:
  filterByProduct(
    product: string,
    entity,
    user: UserAuthentified = undefined,
  ): Question[] {
    const questions =
      this.subscriptionFacade.getProductQuestionsByName(product);

    // Cf. getConsultantQuestionsCallback : isConsultant est un gate de rôle, pas un raccourci de visibilité.
    return questions.filter(
      q =>
        (q.alwaysShow && !!q.alwaysShow(entity, user)) ||
        !q.callbackBasedOn ||
        !q.callbackBasedOn(entity, user),
    );
  }
  protected setQuestionConsultantFirstTime(
    productType: any,
    pledge: PledgeRead,
    user: UserAuthentified = undefined,
  ) {
    this.questionConsultant = this.questionConsultant
      ? this.questionConsultant
      : this.subscriptionFacade.getConsultantQuestionsFirstTime({
          product: productType,
          entity: pledge,
          user,
        });
  }

  protected setPictureFile(user: UserAuthentified) {
    const url = user?.sessionConsultant?.consultantProfilePictureUrl;

    this.pictureFile = url ? this.consultantBackUrl + url : '';
  }

  protected displayAndSkipQuestionForConsultant(
    pledge: PledgeRead,
    user: UserAuthentified,
    productType: any,
  ) {
    if (pledge?.owners?.[0]) {
      pledge.owners[0].receivePreContractElementsEmail = true;
      const step = pledge?.step;

      this.skipQuestionForConsultant(user, productType, pledge);
    }

    this.subscriptionFacade.setPledge(pledge);
  }

  protected displayInvalidQuestionForClient(
    user: UserAuthentified,
    pledge: PledgeRead,
    productType: any,
  ) {
    // this.subscriptionFacade.setStep(1);
    // let stepExist = false;
    // if (!user.sessionConsultant) {
    //   if (pledge?.owners?.[0]) {
    //     const step = pledge.step;
    //     if (step) {
    //       stepExist = true;
    //     }
    //   }
    // }
    // if (stepExist) {
    //   this.setStepToInValidQuestion(productType, pledge, this.step);
    // }
    if (!user?.sessionConsultant) {
      this.subscriptionFacade.setStep(1);
    }
  }

  protected skipQuestionForConsultant(
    user: UserAuthentified,
    productType: string,
    pledge: PledgeRead,
  ) {
    const questions = this.getQuestions(user, productType, pledge);
    const qId = questions[0]?.id;
    const qs = this.filterByProduct(productType, pledge, user);
    const stepQ = qs?.findIndex(q => q?.id == qId);

    if (stepQ && stepQ != -1) {
      this.subscriptionFacade.setStep(stepQ + 1);
    }
  }
  protected setStepToInValidQuestion(
    productType: ProductsTypeEnum,
    pledge: PledgeRead,
    step,
  ) {
    const questions = this.subscriptionFacade.getClientQuestions({
      product: productType,
      entity: pledge,
    });
    const question = questions.find(
      q =>
        q.callbackValidation &&
        !q?.callbackValidation(pledge, this.subscriptionFacade.getUser()),
    );

    const qId = question?.id;
    const stepQ = questions?.findIndex(q => q?.id == qId);
    if (stepQ != -1 && stepQ <= step) {
      this.subscriptionFacade.setStep(stepQ + 1);
    }
  }

  getQuestionByCategory(questions: Question[], question: Question): Question[] {
    return questions?.filter(q => q.category === question?.category);
  }
  findIndexQuestionCategory(
    questions: Question[] = [],
    question: Question,
  ): number {
    const qs = this.getQuestionByCategory(questions, question);
    return qs?.findIndex(q => q?.id === question?.id);
  }

  removeExtraPercent(text: string): string {
    return text?.replace('%%', '%');
  }
  notifyClick() {
    this.clickSubject.next(1);
  }
}
